/**
 * Shared validation for the image pickers (product image, company logo, banner).
 *
 * The three call sites used to carry their own copies of this, which had drifted
 * apart: the product picker accepted a file whose extension matched even when the
 * browser reported no MIME type, the logo and banner pickers rejected it. Mobile
 * file pickers are exactly where that matters — several Android pickers hand over
 * a file with an empty `type`, and iOS reports `image/heic` for camera roll
 * originals.
 */

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
];

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];

/** 5 MB — matches what the backend accepts. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * @returns a German error message, or `null` when the file looks usable.
 *
 * A file is accepted when EITHER the MIME type OR the extension matches, so a
 * picker that reports no type at all doesn't block an obviously fine .jpg. If
 * such a file turns out to be undecodable, the cropper's load error catches it
 * and says so — better than rejecting a valid photo up front.
 */
export function validateImageFile(file: File, maxBytes = MAX_IMAGE_BYTES): string | null {
  const dot = file.name.lastIndexOf('.');
  const extension = dot >= 0 ? file.name.substring(dot).toLowerCase() : '';
  const typeOk = ALLOWED_TYPES.includes(file.type.toLowerCase());
  const extensionOk = ALLOWED_EXTENSIONS.includes(extension);

  if (!typeOk && !extensionOk) {
    return 'Ungültiges Dateiformat. Bitte wähle ein JPG-, PNG-, HEIC-, GIF- oder WEBP-Bild.';
  }

  if (file.size > maxBytes) {
    const mb = Math.round(maxBytes / (1024 * 1024));
    return `Die Datei ist zu groß. Maximale Größe: ${mb} MB.`;
  }

  return null;
}

/**
 * Reads the picked file and clears the input in one go.
 *
 * Clearing is the important half: a file input only fires `change` when its
 * value actually changes, so without this, picking the *same* file a second
 * time does nothing at all. That happens constantly on phones — crop, dislike
 * the result, tap the same photo again, and the app appears frozen.
 */
export function takeSelectedFile(event: Event): File | null {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0] ?? null;
  if (input) input.value = '';
  return file;
}
